import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Phone, MessageCircle, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Reveal from "@/components/Reveal";
import { STORE } from "@/data/store";
import { CATEGORIES, PRODUCTS } from "@/data/products";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const setCat = (cat) => {
    if (cat === "all") params.delete("cat");
    else params.set("cat", cat);
    setParams(params, { replace: true });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      const inCat = activeCat === "all" || p.category === activeCat;
      const inQuery = !q || p.name.toLowerCase().includes(q) || p.origin.toLowerCase().includes(q);
      return inCat && inQuery;
    });
  }, [activeCat, query]);

  const catImage = (id) => CATEGORIES.find((c) => c.id === id)?.image;
  const catLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label;

  return (
    <div data-testid="products-page" className="pt-32 lg:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        {/* Header */}
        <Reveal>
          <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">Products &amp; Gallery</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] max-w-3xl">
            Everything on our shelves, honestly labelled.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mt-6">
            {PRODUCTS.length} handpicked staples across {CATEGORIES.length} aisles. Prices are today's
            counter rates — daily produce may vary with the mandi.
          </p>
        </Reveal>

        {/* Search + Filters */}
        <Reveal delay={0.1} className="mt-12">
          <div className="relative max-w-md">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ghee, basmati, chilli..."
              data-testid="product-search-input"
              className="w-full rounded-full border border-border bg-card pl-11 pr-5 py-3.5 text-sm outline-none focus:border-terra transition-colors duration-300 placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex flex-wrap gap-2.5 mt-6" data-testid="category-filter-row">
            <button
              type="button"
              onClick={() => setCat("all")}
              data-testid="category-filter-all"
              className={`rounded-full px-5 py-2.5 font-sub text-xs font-semibold tracking-wide border transition-colors duration-300 ${
                activeCat === "all"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-terra hover:text-terra"
              }`}
            >
              All ({PRODUCTS.length})
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                data-testid={`category-filter-${c.id}`}
                className={`rounded-full px-5 py-2.5 font-sub text-xs font-semibold tracking-wide border transition-colors duration-300 ${
                  activeCat === c.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:border-terra hover:text-terra"
                }`}
              >
                {c.label} ({PRODUCTS.filter((p) => p.category === c.id).length})
              </button>
            ))}
          </div>
        </Reveal>

        {/* Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-12" data-testid="product-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`product-card-${p.id}`}
                className="group rounded-3xl border border-border bg-card overflow-hidden hover:border-terra/50 hover:shadow-xl hover:shadow-terra/5 transition-all duration-300 cursor-pointer"
                onClick={() => setSelected(p)}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={catImage(p.category)}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-background/90 backdrop-blur px-3.5 py-1.5 font-code text-[10px] uppercase tracking-[0.2em] text-terra">
                    {p.badge}
                  </span>
                </div>
                <div className="p-6">
                  <p className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{catLabel(p.category)}</p>
                  <div className="flex items-start justify-between gap-3 mt-2">
                    <h3 className="font-display text-xl font-semibold leading-snug">{p.name}</h3>
                    <p className="text-right shrink-0">
                      <span className="font-display text-xl font-bold text-terra">₹{p.price}</span>
                      <span className="block font-code text-[10px] uppercase tracking-wider text-muted-foreground">{p.unit}</span>
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2 line-clamp-2">{p.description}</p>
                  <p className="font-code text-[11px] tracking-wider text-muted-foreground mt-4 flex items-center gap-2">
                    <Package size={12} className="text-terra" /> Origin: {p.origin}
                  </p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <div className="text-center py-20" data-testid="product-empty-state">
            <p className="font-display text-2xl">Nothing matches that search.</p>
            <p className="text-sm text-muted-foreground mt-2">Try another name, or call us — if it exists in a mandi, we'll find it for you.</p>
          </div>
        )}

        {/* Bulk order banner */}
        <Reveal className="mt-20">
          <div
            data-testid="bulk-order-banner"
            className="rounded-[2.5rem] border border-border bg-secondary/60 p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8"
          >
            <div>
              <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-3">Wholesale &amp; Bulk</p>
              <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight leading-snug max-w-xl">
                Weddings, hostels, halwais and hotels — we supply in bulk at mandi-linked rates.
              </h2>
            </div>
            <a
              href={`https://wa.me/${STORE.phoneRaw}?text=${encodeURIComponent("Namaste! I'd like to enquire about a bulk grocery order.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bulk-order-whatsapp-button"
              className="group inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-7 py-4 font-sub text-sm font-semibold shrink-0 hover:bg-terra transition-colors duration-300"
            >
              <MessageCircle size={16} />
              Enquire on WhatsApp
            </a>
          </div>
        </Reveal>
      </div>

      {/* Product detail modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="product-detail-modal" className="sm:max-w-lg rounded-3xl p-0 overflow-hidden">
          {selected && (
            <div>
              <div className="relative aspect-[16/9]">
                <img src={catImage(selected.category)} alt={selected.name} className="w-full h-full object-cover" />
                <span className="absolute top-4 left-4 rounded-full bg-background/90 backdrop-blur px-3.5 py-1.5 font-code text-[10px] uppercase tracking-[0.2em] text-terra">
                  {selected.badge}
                </span>
              </div>
              <div className="p-7">
                <DialogHeader>
                  <p className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{catLabel(selected.category)} · {selected.origin}</p>
                  <DialogTitle className="font-display text-3xl font-semibold mt-1">{selected.name}</DialogTitle>
                  <DialogDescription className="text-sm text-muted-foreground leading-relaxed mt-2">
                    {selected.description}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-baseline gap-2 mt-5">
                  <span className="font-display text-3xl font-bold text-terra" data-testid="modal-product-price">₹{selected.price}</span>
                  <span className="font-code text-xs uppercase tracking-wider text-muted-foreground">{selected.unit}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {selected.packs.map((pack) => (
                    <span key={pack} className="rounded-full border border-border px-4 py-1.5 font-code text-xs tracking-wide">{pack}</span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mt-7">
                  <a
                    href={`https://wa.me/${STORE.phoneRaw}?text=${encodeURIComponent(`Namaste! I'd like to order ${selected.name} (${selected.unit}).`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="modal-order-whatsapp-button"
                    className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full bg-primary text-primary-foreground px-6 py-3.5 font-sub text-sm font-semibold hover:bg-terra transition-colors duration-300"
                  >
                    <MessageCircle size={16} /> Order on WhatsApp
                  </a>
                  <a
                    href={`tel:+${STORE.phoneRaw}`}
                    data-testid="modal-order-call-button"
                    className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full border border-foreground/25 px-6 py-3.5 font-sub text-sm font-semibold hover:border-terra hover:text-terra transition-colors duration-300"
                  >
                    <Phone size={16} /> Call to Order
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
